import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useUploadQueue } from '../../context/UploadQueueContext';

const UploadZone = forwardRef(function UploadZone({
  siteId,
  onUploaded,
  showControls = true,
  targetFolder = '',
  accept = '',
}, ref) {
  const { items, enqueueFiles, updateTitle, cancelItem, clearCompleted, removeItem } = useUploadQueue();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handler = (evt) => {
      if (evt?.detail?.siteId && siteId && String(evt.detail.siteId) !== String(siteId)) return;
      if (targetFolder && String(evt?.detail?.folder || '').trim() !== String(targetFolder || '').trim()) return;
      onUploaded?.();
    };
    window.addEventListener('uploadQueue:itemDone', handler);
    return () => window.removeEventListener('uploadQueue:itemDone', handler);
  }, [onUploaded, siteId, targetFolder]);

  const siteItems = useMemo(() => {
    if (!siteId) return [];
    return items.filter((item) => {
      if (String(item.siteId) !== String(siteId)) return false;
      if (targetFolder) {
        return String(item.folder || '').trim() === String(targetFolder).trim();
      }
      return true;
    });
  }, [items, siteId, targetFolder]);
  const visibleItems = useMemo(
    () => siteItems.filter((item) => item.status !== 'done'),
    [siteItems]
  );

  const totals = useMemo(() => {
    const relevant = siteItems.filter((item) => item.status !== 'canceled');
    const totalBytes = relevant.reduce((sum, item) => sum + (item.total || item.size || 0), 0);
    const doneBytes = relevant.reduce((sum, item) => {
      if (item.status === 'done') return sum + (item.total || item.size || 0);
      return sum + Math.min(item.loaded || 0, item.total || item.size || 0);
    }, 0);
    const queuedCount = relevant.filter((i) => i.status === 'queued').length;
    const active = relevant.find((i) => ['presigning', 'uploading', 'finalizing'].includes(i.status)) || null;
    return { totalBytes, doneBytes, queuedCount, active };
  }, [siteItems]);

  const totalPct = totals.totalBytes ? Math.min(100, Math.round((totals.doneBytes / totals.totalBytes) * 100)) : 0;
  const hasCompleted = siteItems.some((item) => item.status === 'done');

  useImperativeHandle(ref, () => ({
    openFileDialog: () => fileInputRef.current?.click(),
    clearCompleted: () => clearCompleted(),
    hasCompleted,
  }), [clearCompleted, hasCompleted]);

  const formatBytes = (bytes) => {
    const value = Number(bytes) || 0;
    if (value < 1024) return `${value} B`;
    const kb = value / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg text-white">
      <div className="flex flex-col gap-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept || undefined}
          className="hidden"
          onChange={(e) => {
            if (!siteId) return;
            const selected = Array.from(e.target.files || []);
            if (!selected.length) return;
            enqueueFiles(siteId, selected, { folder: targetFolder });
            e.target.value = '';
          }}
        />
        {showControls && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="px-3 py-1 bg-gray-700 rounded text-white"
              onClick={() => {
                fileInputRef.current?.click();
              }}
              disabled={!siteId}
            >
              Upload
            </button>
            <button
              className="px-3 py-1 bg-gray-700 rounded text-white"
              onClick={clearCompleted}
              disabled={!hasCompleted}
            >
              Clear Completed
            </button>
          </div>
        )}

        {siteItems.length > 0 && (
          <div className="bg-gray-900 rounded p-3">
            <div className="flex items-center justify-between text-sm text-gray-200">
              <div>
                Total progress: {totalPct}% ({formatBytes(totals.doneBytes)} / {formatBytes(totals.totalBytes)})
              </div>
              <div>{totals.queuedCount ? `${totals.queuedCount} queued` : totals.active ? `Working…` : 'Idle'}</div>
            </div>
            <div className="mt-2 w-full bg-gray-700 rounded h-2 overflow-hidden">
              <div
                className="bg-indigo-500 h-2"
                style={{ width: `${totalPct}%` }}
              />
            </div>
          </div>
        )}

        {siteItems.length === 0 && (
          <div className="text-gray-300 text-sm">
            Select one or more files to upload{targetFolder ? ` into ${targetFolder}` : ''}. Uploads continue if you switch control panel tabs.
          </div>
        )}

        {visibleItems.length > 0 && (
          <div className="space-y-2">
            {visibleItems.map((item) => {
              const pct = item.total ? Math.min(100, Math.round(((item.loaded || 0) / item.total) * 100)) : 0;
              const canEditTitle = item.status === 'queued';
              const canCancel = ['queued', 'presigning', 'uploading', 'finalizing', 'interrupted', 'error'].includes(item.status);
              const statusLabel = item.status === 'presigning'
                ? 'Preparing…'
                : item.status === 'uploading'
                  ? `Uploading… ${pct}%`
                  : item.status === 'finalizing'
                    ? 'Finalizing…'
                    : item.status === 'queued'
                      ? 'Queued'
                      : item.status === 'done'
                        ? 'Done'
                        : item.status === 'interrupted'
                          ? 'Interrupted (re-add file)'
                          : item.status === 'error'
                            ? 'Error'
                            : item.status;

              return (
                <div key={item.id} className="bg-gray-900 rounded p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{item.filename}</div>
                      <div className="text-xs text-gray-300">{statusLabel}</div>
                      {item.error && <div className="text-xs text-red-300 mt-1">{item.error}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      {canCancel && (
                        <button
                          className="px-3 py-1 bg-red-700 rounded text-white"
                          onClick={() => cancelItem(item.id)}
                        >
                          Cancel
                        </button>
                      )}
                      {item.status === 'error' && (
                        <button
                          className="px-3 py-1 bg-gray-700 rounded text-white"
                          onClick={() => removeItem(item.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-2">
                    <label className="block text-xs text-gray-300 mb-1">Title</label>
                    <input
                      className="w-full px-2 py-1 rounded bg-gray-800 text-white border border-gray-700"
                      value={item.title || ''}
                      disabled={!canEditTitle}
                      onChange={(e) => updateTitle(item.id, e.target.value)}
                    />
                  </div>

                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-300">
                      <div>{formatBytes(item.loaded)} / {formatBytes(item.total || item.size)}</div>
                      <div>{item.status === 'uploading' ? `${pct}%` : null}</div>
                    </div>
                    <div className="mt-1 w-full bg-gray-700 rounded h-2 overflow-hidden">
                      <div
                        className={`h-2 ${item.status === 'done' ? 'bg-green-500' : item.status === 'error' ? 'bg-red-500' : 'bg-indigo-500'}`}
                        style={{ width: `${item.status === 'done' ? 100 : pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default UploadZone;
